import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
    ApplicantInfo,
    PaginatedApplicationList,
    ApplicationProgressResponse,
    Eligibility,
    EligibilityAttestation,
    MedicareInfo,
    Payment,
    Pcp,
    PlanSelection,
    Review,
} from "@core/interfaces/api-responses";
import { PriceBook } from "@core/interfaces/entities/price-book";
import { EnrollmentPeriods } from "@core/interfaces/api-responses/enrollment-periods";

@Injectable({
    providedIn: "root",
})
export class ApplicationService {
    private root = `/api/applications`;

    constructor(private httpClient: HttpClient) {}

    transformStep = (stepName: string) => stepName.toLowerCase().replace(/-/g, "_");

    getStepData(
        applicationId: string,
        stepName: string = "",
    ): Observable<Eligibility | EligibilityAttestation | PlanSelection | ApplicantInfo | MedicareInfo | Pcp | Payment> {
        return this.httpClient.get<Eligibility | EligibilityAttestation | PlanSelection | ApplicantInfo | MedicareInfo | Pcp | Payment>(
            `${this.root}/${applicationId}/step`,
            {
                params: { stepName: this.transformStep(stepName) },
            },
        );
    }

    updateStep(
        applicationId: number,
        stepName: string = "",
        payload: Eligibility | EligibilityAttestation | PlanSelection | ApplicantInfo | MedicareInfo | Pcp | Payment | Review | null = null,
    ): Observable<ApplicationProgressResponse> {
        const requestURL = applicationId ? `${this.root}/${applicationId}/step` : `${this.root}/step`;
        return this.httpClient.post<ApplicationProgressResponse>(requestURL, payload, {
            params: { stepName: this.transformStep(stepName) },
        });
    }

    getProgress(applicationId: number): Observable<ApplicationProgressResponse> {
        return this.httpClient.get<ApplicationProgressResponse>(`${this.root}/progress/${applicationId}`);
    }

    getPlans(applicationId: number): Observable<PriceBook[]> {
        return this.httpClient.get<PriceBook[]>(`${this.root}/${applicationId}/plans`);
    }

    getApplicationList(params?: HttpParams): Observable<PaginatedApplicationList> {
        return this.httpClient.get<PaginatedApplicationList>(`${this.root}/list`, {
            ...(params ? { params } : {}),
        });
    }

    resolveApplicationYear(effectiveDate: string): Observable<ApplicationProgressResponse> {
        return this.httpClient.get<ApplicationProgressResponse>(`${this.root}/resolve/${effectiveDate}`);
    }

    getEnrollmentPeriods(applicationId: string): Observable<EnrollmentPeriods> {
        return this.httpClient.get<EnrollmentPeriods>(`${this.root}/${applicationId}/enrollment-periods`, {});
    }
}
